// =====================================================================
// DefaultAvatar — the ONE deterministic, storage-free default avatar.
//
// Given {seed, role?, gender?, name?, size?} it renders an attractive
// gradient SVG disc + initials. Same input → same output, every time;
// no network, no CDN, no DB, no data-URI persistence.
//
// HOW THE COLOUR IS DERIVED (3 layers, in order of strength):
//   1. BASE PALETTE  — from the role colour (role.tsx ROLES[x].color is
//      one of cyan|amber|violet|rose). Each maps to a base HUE so a
//      student disc reads cyan-ish, an instructor amber-ish, etc. When
//      no role is known we fall back to a neutral accent-ish hue.
//   2. SEED HUE ROTATION — a deterministic hash of `seed` (mirrors the
//      good hash in pages/Messages.tsx hueOf) nudges the base hue by a
//      bounded amount, so two students still get visibly different discs
//      while staying inside their role's colour family.
//   3. GENDER TINT — when gender is known we shift saturation/lightness
//      and the gradient angle a touch (a subtle variant, NOT a
//      stereotyped pink/blue): female = warmer + slightly lighter,
//      male = cooler + slightly deeper, unspecified/unknown = neutral.
//
// The result is a 135°-ish two-stop linear gradient rendered as an SVG
// (crisp at any size, theme-independent since it's self-contained HSL).
//
// INITIALS: 1–2 letters from `name` (RTL-safe: we take the first letter
// of the first one/two whitespace- or separator-delimited tokens, which
// works for Persian and Latin alike). Fallback «؟» when there's no name.
// =====================================================================
import React from "react";

import type { RoleId } from "../role";

// --- role colour → base hue ------------------------------------------------
// role.tsx uses four colour tokens. Map each to a representative HUE that
// matches the styles.css .avatar.<color> gradients (cyan ~200, amber ~75,
// violet ~285/305, rose ~20). A neutral accent-ish hue covers "no role".
const ROLE_COLOR_HUE: Record<string, number> = {
  cyan: 200,
  amber: 75,
  violet: 288,
  rose: 18,
};
const NEUTRAL_HUE = 210; // accent-ish blue when role/colour is unknown

// We accept either a role id (we look up its colour) or a colour token
// directly. Keep it loose (string) so callers needn't import RoleId.
import { ROLES } from "../role";

const baseHueFor = (role?: string): number => {
  if (!role) return NEUTRAL_HUE;
  // direct colour token?
  if (role in ROLE_COLOR_HUE) return ROLE_COLOR_HUE[role];
  // role id → its colour token → hue
  const entry = ROLES[role as RoleId];
  if (entry && entry.color in ROLE_COLOR_HUE) return ROLE_COLOR_HUE[entry.color];
  return NEUTRAL_HUE;
};

// --- deterministic seed hash (mirrors Messages.tsx hueOf) ------------------
// Same FNV-ish rolling hash → a stable 32-bit unsigned int from any string.
const hashSeed = (seed: string): number => {
  let h = 0;
  const s = seed || "?";
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
};

// --- gender variant --------------------------------------------------------
// A SUBTLE, non-stereotyped tint: female = a touch warmer + lighter, male =
// a touch cooler + deeper, everything else neutral. Values are small so the
// role family still dominates.
interface GenderVariant {
  hueShift: number; // degrees nudged on top of the role+seed hue
  satMul: number; // saturation multiplier
  lightMul: number; // lightness multiplier
  angle: number; // gradient angle (deg)
}
const genderVariant = (gender?: string): GenderVariant => {
  switch (gender) {
    case "female":
      return { hueShift: 10, satMul: 1.06, lightMul: 1.06, angle: 150 };
    case "male":
      return { hueShift: -10, satMul: 1.0, lightMul: 0.94, angle: 120 };
    default: // "unspecified" | undefined | unknown
      return { hueShift: 0, satMul: 1.0, lightMul: 1.0, angle: 135 };
  }
};

// --- initials (RTL-safe) ---------------------------------------------------
export const initialsOf = (name?: string | null): string => {
  const n = (name ?? "").trim();
  if (!n) return "؟";
  const parts = n.split(/[\s.\-_]+/).filter(Boolean);
  if (parts.length >= 2) {
    return ((parts[0][0] ?? "") + (parts[1][0] ?? "")) || "؟";
  }
  // Single token: first 1–2 chars (Latin "Jo", Persian first letter).
  return n.slice(0, 2) || "؟";
};

export interface DefaultAvatarProps {
  /** Stable identity key (e.g. userId). Drives the deterministic hue. */
  seed: string;
  /** Role id ("student"…) OR a colour token ("cyan"…) for the base palette. */
  role?: string;
  /** "male" | "female" | "unspecified" — a subtle tint when known. */
  gender?: string;
  /** Display name → initials. */
  name?: string | null;
  /** Pixel diameter. Default 40. */
  size?: number;
  /** Extra className on the wrapper (sizing/positioning by the caller). */
  className?: string;
  /** Inline style override on the wrapper (rarely needed). */
  style?: React.CSSProperties;
  /** Accessible label; defaults to the name (or hidden when decorative). */
  title?: string;
}

/**
 * Compute the two gradient stops + the angle for a given identity. Exported
 * so a caller that needs the raw colour (e.g. a CSS background on an existing
 * node) can reuse the exact same deterministic palette.
 */
export const defaultAvatarGradient = (
  seed: string,
  role?: string,
  gender?: string,
): { from: string; to: string; angle: number } => {
  const base = baseHueFor(role);
  const h = hashSeed(seed);
  const g = genderVariant(gender);
  // Seed nudges the hue inside a bounded ±28° window so discs vary but stay
  // in the role's colour family; then the gender shift on top.
  const hue = (base + ((h % 57) - 28) + g.hueShift + 360) % 360;
  const sat1 = Math.min(85, Math.round(64 * g.satMul));
  const sat2 = Math.min(88, Math.round(70 * g.satMul));
  const light1 = Math.min(72, Math.round(60 * g.lightMul));
  const light2 = Math.max(34, Math.round(46 * g.lightMul));
  return {
    from: `hsl(${hue} ${sat1}% ${light1}%)`,
    to: `hsl(${(hue + 26) % 360} ${sat2}% ${light2}%)`,
    angle: g.angle,
  };
};

export const DefaultAvatar: React.FC<DefaultAvatarProps> = ({
  seed,
  role,
  gender,
  name,
  size = 40,
  className,
  style,
  title,
}) => {
  const { from, to, angle } = defaultAvatarGradient(seed, role, gender);
  const text = initialsOf(name);
  // A unique gradient id per render input so two avatars on the page never
  // collide on the same <defs> id.
  const gid = React.useId();
  // Initials font scales with the disc; Persian glyphs read a touch larger.
  const fontSize = Math.round(size * 0.4);

  return (
    <span
      className={className}
      style={{
        display: "inline-block",
        width: size,
        height: size,
        flexShrink: 0,
        lineHeight: 0,
        ...style,
      }}
      role="img"
      aria-label={title ?? (name ? String(name) : undefined)}
      aria-hidden={title || name ? undefined : true}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: "block", borderRadius: "50%" }}
      >
        <defs>
          <linearGradient
            id={`dav-${gid}`}
            gradientTransform={`rotate(${angle} 0.5 0.5)`}
            x1="0"
            y1="0"
            x2="1"
            y2="0"
          >
            <stop offset="0%" stopColor={from} />
            <stop offset="100%" stopColor={to} />
          </linearGradient>
        </defs>
        <circle cx="50" cy="50" r="50" fill={`url(#dav-${gid})`} />
        {/* subtle top sheen for depth — pure decoration */}
        <ellipse cx="50" cy="30" rx="42" ry="26" fill="rgba(255,255,255,0.12)" />
        <text
          x="50"
          y="50"
          textAnchor="middle"
          dominantBaseline="central"
          fontFamily="Vazirmatn, system-ui, sans-serif"
          fontSize={(fontSize / size) * 100}
          fontWeight={700}
          fill="#fff"
          style={{ direction: "rtl" }}
        >
          {text}
        </text>
      </svg>
    </span>
  );
};

export default DefaultAvatar;
